import { useState } from 'react';
import { CheckCircle2, ClipboardCopy, Loader2, KeyRound } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import {
  generateRsaKeys,
  type GenerateOpts,
  type RsaHash,
  type RsaModulusBits,
  type RsaScheme,
} from '../lib/rsa';

const BITS: RsaModulusBits[] = [2048, 3072, 4096];
const HASHES: RsaHash[] = ['SHA-256', 'SHA-384', 'SHA-512'];

const SCHEME_LABEL: Record<RsaScheme, string> = {
  oaep: 'OAEP（加解密）',
  pss: 'PSS（签名）',
  pkcs1: 'PKCS1-v1_5（签名）',
};

/**
 * `/tools/crypto/rsa-keygen` — RSA key-pair generator.
 *
 * Three schemes:
 *   - `oaep` 对应 RSA-OAEP，可以用于 `/tools/crypto/rsa` 加解密；
 *   - `pss` / `pkcs1` 用于 `/tools/crypto/rsa` 的签名 / 验签 tab。
 *
 * PEM 输出 PKCS#8（私钥）+ SPKI（公钥），跟 OpenSSL `genpkey -algorithm RSA`
 * 默认产物互通；导入到 `crypto.subtle.importKey` 时也走对应的格式。
 */
export default function RsaKeygenTool(): JSX.Element {
  const [scheme, setScheme] = useState<RsaScheme>('oaep');
  const [hash, setHash] = useState<RsaHash>('SHA-256');
  const [bits, setBits] = useState<RsaModulusBits>(2048);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keys, setKeys] = useState<{ privatePem: string; publicPem: string } | null>(null);
  const [copied, setCopied] = useState<'priv' | 'pub' | null>(null);

  const onGen = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setKeys(null);
    try {
      const opts: GenerateOpts = { scheme, hash, bits };
      const r = await generateRsaKeys(opts);
      setKeys(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (which: 'priv' | 'pub'): Promise<void> => {
    if (!keys) return;
    const text = which === 'priv' ? keys.privatePem : keys.publicPem;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 900);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="rsa-keygen"
        category="crypto"
        description="生成 RSA 密钥对（PKCS#8 + SPKI PEM）。OAEP 用于加解密，PSS / PKCS1 用于签名。所有运算走浏览器 SubtleCrypto，私钥不离开本地。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">用途</span>
        <div className="flex flex-wrap gap-1.5">
          {(['oaep', 'pss', 'pkcs1'] as RsaScheme[]).map((s) => (
            <Pill key={s} active={scheme === s} onClick={() => setScheme(s)}>
              {SCHEME_LABEL[s]}
            </Pill>
          ))}
        </div>
        <span className="ml-3 text-[11px] uppercase tracking-wide text-ink-dim">位数</span>
        <div className="flex gap-1.5">
          {BITS.map((b) => (
            <Pill key={b} active={bits === b} onClick={() => setBits(b)}>
              {b}
            </Pill>
          ))}
        </div>
        <span className="ml-3 text-[11px] uppercase tracking-wide text-ink-dim">摘要</span>
        <div className="flex gap-1.5">
          {HASHES.map((h) => (
            <Pill key={h} active={hash === h} onClick={() => setHash(h)}>
              {h}
            </Pill>
          ))}
        </div>
      </section>

      <section className="card-pad flex items-center gap-3">
        <button
          type="button"
          onClick={onGen}
          disabled={busy}
          className="btn"
        >
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              生成中…（{bits} 位 RSA 密钥可能耗时 1–10s）
            </>
          ) : (
            <>
              <KeyRound className="h-3.5 w-3.5" />
              生成密钥对
            </>
          )}
        </button>
        <span className="text-[11px] text-ink-dim">
          位数越大耗时越长；4096 位在常见笔电上约 5–15 s。
        </span>
      </section>

      {error && (
        <div className="card-pad border-red-500/30 bg-red-500/5 text-[12px] text-red-500">
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
        <PemPane
          title="私钥（PKCS#8）"
          pem={keys?.privatePem ?? ''}
          copied={copied === 'priv'}
          onCopy={() => onCopy('priv')}
          warn="妥善保管 — 任何拿到该私钥的人都能解密 / 签名以你的身份。"
        />
        <PemPane
          title="公钥（SPKI）"
          pem={keys?.publicPem ?? ''}
          copied={copied === 'pub'}
          onCopy={() => onCopy('pub')}
        />
      </div>
    </div>
  );
}

function PemPane({
  title,
  pem,
  copied,
  onCopy,
  warn,
}: {
  title: string;
  pem: string;
  copied: boolean;
  onCopy: () => void;
  warn?: string;
}): JSX.Element {
  return (
    <section className="card-pad flex min-h-0 flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">{title}</span>
        <button
          type="button"
          onClick={onCopy}
          disabled={!pem}
          className="btn-ghost px-2 py-0.5"
          title="复制"
        >
          {copied ? (
            <>
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              已复制
            </>
          ) : (
            <>
              <ClipboardCopy className="h-3 w-3" />
              复制
            </>
          )}
        </button>
      </header>
      <textarea
        className="input flex-1 resize-none font-mono text-[12px]"
        value={pem}
        readOnly
        spellCheck={false}
        placeholder="点击「生成密钥对」后在这里显示 PEM"
      />
      {warn && (
        <p className="text-[11px] text-amber-500">⚠ {warn}</p>
      )}
    </section>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[12px] transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
